import React, { useState, useMemo } from 'react';
import { useStore } from '../store/useStore';
import { KharonCase } from '../types';
import { 
    PlusIcon, 
    CalendarIcon, 
    CheckIcon, 
    ActivityIcon,
    ChevronRightIcon,
    CasesIcon as FilterIcon,
    DocumentTextIcon,
    PencilIcon,
    ExclamationCircleIcon
} from '../components/icons';
import { 
    BarChart, 
    Bar, 
    XAxis, 
    Tooltip, 
    ResponsiveContainer,
    Cell
} from 'recharts';
import { StatCard } from '../components/StatCard';



export const WelcomeScreen: React.FC<{
    onStart: () => void;
    onOpenCase: (service: any) => void;
}> = ({ onStart, onOpenCase }) => {
    const { cases: rawCases, servicePlans, currentUser } = useStore();
    const services = rawCases as KharonCase[];
    const userInfo = currentUser || { name: 'Usuario' };
    const today = new Date();
    const todayStr = today.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });

    const activeServices = services.filter(s => s.status === 'En Proceso');
    const completedThisMonth = services.filter(s => {
        try {
            const [day, month, year] = s.caseDate.split('/');
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear() && s.status === 'Completado';
        } catch(e) { return false; }
    });

    const activeBreakdown = useMemo(() => {
        let inhu = 0;
        let crem = 0;
        activeServices.forEach(s => {
            const plan = servicePlans.find(p => p.id === s.serviceDetails?.planId);
            const name = (plan?.name || '').toLowerCase();
            if (name.includes('cremaci') || name.includes('incinera')) crem++;
            else inhu++;
        });
        return { inhu, crem };
    }, [activeServices, servicePlans]);

    const goal = 10;
    const progressPercent = Math.min(100, Math.round((completedThisMonth.length / goal) * 100));
    
    const weeklyData = useMemo(() => {
        const days = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
        const counts = [0, 0, 0, 0, 0, 0, 0];
        const now = new Date();
        const startOfWeek = new Date(now);
        startOfWeek.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
        startOfWeek.setHours(0,0,0,0);

        services.filter(s => s.status === 'Pendiente').forEach(s => {
            try {
                const [day, month, year] = s.caseDate.split('/');
                const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                if (date >= startOfWeek) {
                    const dayIndex = date.getDay() === 0 ? 6 : date.getDay() - 1;
                    counts[dayIndex] += 1;
                }
            } catch(e) {}
        });
        return days.map((name, i) => ({ name, value: counts[i] }));
    }, [services]);

    const pendingServicesCount = services.filter(s => s.status === 'Pendiente').length;

    return (
        <div className="max-w-7xl mx-auto px-6 py-10 space-y-12">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                    <span className="text-xs font-black uppercase tracking-[0.2em] text-primary-500 mb-2 block">Panel de Control</span>
                    <h1 className="text-4xl font-serif font-bold text-slate-900 dark:text-white">Buenos días, {userInfo.name.split(' ')[0]}</h1>
                    <p className="text-slate-500 mt-1">{todayStr}</p>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={onStart} 
                        className="flex items-center gap-3 px-8 py-4 bg-slate-900 dark:bg-primary-500 text-white dark:text-slate-900 font-bold rounded-2xl shadow-luxury transition-luxury hover:scale-105 active:scale-95"
                    >
                        <PlusIcon />
                        <span>Iniciar Nuevo Expediente</span>
                    </button>
                </div>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                    title="Activos hoy" 
                    value={activeServices.length} 
                    icon={<ActivityIcon className="h-6 w-6" />}
                    detailContent={
                        <div className="space-y-3">
                            <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-slate-400">
                                <span>Inhumación</span>
                                <span>{activeBreakdown.inhu} casos</span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div className="h-full bg-indigo-500" style={{ width: `${(activeBreakdown.inhu / (activeServices.length || 1)) * 100}%` }}></div>
                            </div>
                            <div className="flex justify-between text-[10px] font-black uppercase tracking-widest text-slate-400">
                                <span>Cremación</span>
                                <span>{activeBreakdown.crem} casos</span>
                            </div>
                            <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div className="h-full bg-orange-500" style={{ width: `${(activeBreakdown.crem / (activeServices.length || 1)) * 100}%` }}></div>
                            </div>
                        </div>
                    }
                />
                
                <StatCard 
                    title="Próximos" 
                    value={pendingServicesCount} 
                    icon={<CalendarIcon className="h-6 w-6" />}
                    detailContent={
                        <div className="h-32 w-full">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={weeklyData}>
                                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                        {weeklyData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.value > 0 ? '#3b82f6' : '#e2e8f0'} />
                                        ))}
                                    </Bar>
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 700, fill: '#94a3b8' }} />
                                    <Tooltip 
                                        cursor={{ fill: 'transparent' }} 
                                        content={({ active, payload }) => {
                                            if (active && payload && payload.length) {
                                                return (
                                                    <div className="bg-white dark:bg-slate-800 p-2 shadow-luxury rounded-lg border border-slate-100 dark:border-slate-700">
                                                        <p className="text-[10px] font-black text-slate-900 dark:text-white">{payload[0].value} expedientes</p>
                                                    </div>
                                                );
                                            }
                                            return null;
                                        }}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    }
                />

                <StatCard 
                    title="Meta Mensual" 
                    value={completedThisMonth.length} 
                    icon={<CheckIcon className="h-6 w-6" />}
                    detailContent={
                        <div className="flex flex-col items-center">
                            <div className="relative w-16 h-16 mb-2">
                                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                                    <path className="stroke-slate-200 dark:stroke-slate-700" strokeWidth="3" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                    <path className="stroke-primary-500 transition-all duration-1000" strokeWidth="3" strokeDasharray={`${progressPercent}, 100`} strokeLinecap="round" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center text-[10px] font-black text-slate-600 dark:text-slate-300">{progressPercent}%</div>
                            </div>
                            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Objetivo: {goal} Casos</p>
                        </div>
                    }
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse"></span>
                        Centro de Acción
                    </h2>
                    <div className="bg-white dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-700/50 overflow-hidden shadow-soft p-2">
                        {services
                            .filter(s => s.status !== 'Completado')
                            .sort((a, b) => new Date(b.registrationDate || 0).getTime() - new Date(a.registrationDate || 0).getTime())
                            .slice(0, 5)
                            .map(s => {
                                const isMissingDNI = !s.deceased?.dniNumero && !s.deceased?.dni;
                                const isMissingSignature = !s.applicantSignature;
                                const isMissingPlan = !s.serviceDetails?.planId && !s.serviceDetails?.plan;
                                
                                const hasAlerts = isMissingDNI || isMissingSignature || isMissingPlan;

                            return (
                                <div key={s.id} onClick={() => onOpenCase(s)} className="group flex flex-col md:flex-row justify-between md:items-center p-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 rounded-2xl cursor-pointer transition-all gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-white dark:bg-slate-800 rounded-xl shadow-sm group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 transition-colors">
                                            {hasAlerts ? (
                                                <ExclamationCircleIcon className="h-5 w-5 text-orange-500" />
                                            ) : (
                                                <CheckIcon className="h-5 w-5 text-green-500" />
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-bold text-slate-900 dark:text-white">{s.deceased?.nombre || 'Sin Nombre'} {s.deceased?.apellidos || ''}</p>
                                            <p className="text-xs text-slate-400 mt-0.5 font-mono">{s.caseId}</p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {!hasAlerts && (
                                            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 text-[9px] font-black uppercase tracking-wider">
                                                <span>Listo para Procesar</span>
                                            </div>
                                        )}
                                        {isMissingDNI && (
                                            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400 text-[9px] font-black uppercase tracking-wider">
                                                <DocumentTextIcon className="h-3 w-3" />
                                                <span>Falta DNI</span>
                                            </div>
                                        )}
                                        {isMissingSignature && (
                                            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400 text-[9px] font-black uppercase tracking-wider">
                                                <PencilIcon className="h-3 w-3" />
                                                <span>Sin Firma</span>
                                            </div>
                                        )}
                                        {isMissingPlan && (
                                            <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400 text-[9px] font-black uppercase tracking-wider">
                                                <FilterIcon className="h-3 w-3" />
                                                <span>Plan No Elegido</span>
                                            </div>
                                        )}
                                    </div>
                                    <div className="w-8 h-8 rounded-full border border-slate-100 dark:border-slate-700 flex items-center justify-center transition-luxury group-hover:border-primary-500 group-hover:text-primary-500 hidden md:flex">
                                        <ChevronRightIcon className="h-4 w-4" />
                                    </div>
                                </div>
                            );
                        })}
                        {services.filter(s => s.status !== 'Completado').length === 0 && (
                            <div className="p-10 text-center">
                                <p className="text-sm font-bold text-slate-400">Excelente. Todos los expedientes activos están documentados.</p>
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-6">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-primary-500 rounded-full"></span>
                        Próximos Eventos
                    </h2>
                    <div className="bg-white dark:bg-slate-800/50 rounded-3xl border border-slate-100 dark:border-slate-700/50 p-6 shadow-soft space-y-4">
                        {services
                            .filter(s => s.status !== 'Completado')
                            .filter(s => {
                                // Priorizar fechas de hitos sobre fecha de apertura
                                const dateStr = (s as any).serviceDate || (s as any).inhumationDate || (s as any).cremationDate || s.serviceDetails?.ceremonyDate || s.logistics?.ceremonyDate || s.caseDate;
                                if (!dateStr) return false;
                                
                                try {
                                    const parseRobust = (d: string) => {
                                        if (!d || typeof d !== 'string') return null;
                                        // Caso DD/MM/YYYY
                                        if (d.includes('/')) {
                                            const p = d.split('/');
                                            if (p.length === 3) return new Date(parseInt(p[2]), parseInt(p[1]) - 1, parseInt(p[0]));
                                        }
                                        // Caso YYYY-MM-DD
                                        return new Date(d);
                                    };

                                    const eventDate = parseRobust(dateStr);
                                    if (!eventDate || isNaN(eventDate.getTime())) return false;
                                    
                                    const limit = new Date();
                                    limit.setHours(0,0,0,0);
                                    return eventDate >= limit;
                                } catch(e) { return false; }
                            })
                            .sort((a,b) => {
                                const parseRobust = (d: string) => {
                                    if (!d || typeof d !== 'string') return 0;
                                    const dateStr = d.includes('/') ? d.split('/').reverse().join('-') : d;
                                    const time = new Date(dateStr).getTime();
                                    return isNaN(time) ? 0 : time;
                                };
                                const dateA = (a as any).serviceDate || (a as any).inhumationDate || (a as any).cremationDate || a.serviceDetails?.ceremonyDate || a.logistics?.ceremonyDate || a.caseDate;
                                const dateB = (b as any).serviceDate || (b as any).inhumationDate || (b as any).cremationDate || b.serviceDetails?.ceremonyDate || b.logistics?.ceremonyDate || b.caseDate;
                                return parseRobust(dateA) - parseRobust(dateB);
                            })
                            .slice(0, 3)
                            .map(s => {
                                const displayDate = (s as any).serviceDate || (s as any).inhumationDate || (s as any).cremationDate || s.serviceDetails?.ceremonyDate || s.logistics?.ceremonyDate || s.caseDate;
                                return (
                                <div key={s.id} onClick={() => onOpenCase(s)} className="flex gap-4 p-4 rounded-2xl bg-white dark:bg-slate-900/50 border border-transparent hover:border-primary-200 transition-luxury cursor-pointer shadow-sm">
                                    <div className="w-12 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 flex flex-col items-center justify-center shadow-inner border border-slate-100 dark:border-slate-700">
                                        <span className="text-[10px] font-black text-primary-500 leading-none uppercase tracking-tighter">
                                            {displayDate.includes('/') ? new Date(2000, parseInt(displayDate.split('/')[1]) - 1).toLocaleString('es', { month: 'short' }) : new Date(displayDate).toLocaleString('es', { month: 'short' })}
                                        </span>
                                        <span className="text-xl font-bold text-slate-900 dark:text-white leading-none mt-0.5">
                                            {displayDate.includes('/') ? displayDate.split('/')[0] : displayDate.split('-')[2]?.split('T')[0] || displayDate.split('-')[2] || '--'}
                                        </span>
                                    </div>
                                <div className="min-w-0 flex-grow">
                                    <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{s.deceased?.nombre} {s.deceased?.apellidos}</p>
                                    <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest mt-0.5 mb-2">
                                        {s.serviceDetails?.planId || s.serviceDetails?.plan ? (servicePlans.find(p => p.id === s.serviceDetails!.planId)?.name || s.serviceDetails?.plan) : 'Sin plan adscrito'}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest ${s.status === 'Pendiente' ? 'bg-blue-50 text-blue-500' : 'bg-orange-50 text-orange-500'}`}>
                                            {s.status}
                                        </span>
                                    </div>
                                </div>
                                <div className="self-center text-slate-300">
                                    <ChevronRightIcon className="h-4 w-4" />
                                </div>
                            </div>
                        );
                    })}
                        {services.filter(s => s.status !== 'Completado').length === 0 && (
                             <p className="text-xs font-bold text-slate-400 text-center py-6">No hay servicios próximos o activos.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
